import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Building2, Truck, Calendar, Package, MapPin, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { useRouteDisplay } from '@/utils/routeUtils';

interface CustomerData {
  id: string;
  name: string;
  username?: string;
  type: string;
  description?: string;
  active_connections_count: number;
}

interface LoadOrderData {
  id: string;
  title: string;
  description?: string;
  status: string;
  vehicles_needed: number;
  vehicles_assigned: number;
  vehicles_completed: number;
  vehicles_available: number;
  cargo_type: string;
  cargo_weight?: number;
  created_at: string;
  load_routes: Array<{
    id: string;
    pickup_city: string;
    delivery_city: string;
    pickup_date: string;
    vehicles_needed: number;
    status: string;
  }>;
}

export const CustomerProfilePage = () => {
  const { username } = useParams<{ username: string }>();
  const { user, loading: authLoading } = useAuth();
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [loadOrders, setLoadOrders] = useState<LoadOrderData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { formatRoute } = useRouteDisplay();

  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!username) return;

      try {
        // Fetch customer profile
        const { data: customerData, error: customerError } = await supabase
          .rpc('get_public_profile', { username_input: username });

        if (customerError || !customerData || customerData.length === 0) {
          setError('Customer not found');
          setLoading(false);
          return;
        }

        const customer = customerData[0];
        setCustomer({
          id: customer.organization_id,
          name: customer.name,
          username: customer.username,
          type: customer.organization_type,
          description: '',
          active_connections_count: 0,
        });

        // Fetch customer's load orders if user is authenticated
        if (user) {
          const { data: loadData, error: loadError } = await supabase
            .from('load_orders')
            .select(`
              id,
              title,
              description,
              status,
              vehicles_needed,
              vehicles_assigned,
              vehicles_completed,
              vehicles_available,
              cargo_type,
              cargo_weight,
              created_at,
              load_routes (
                id,
                pickup_city,
                delivery_city,
                pickup_date,
                vehicles_needed,
                status
              )
            `)
            .eq('customer_org_id', customer.organization_id)
            .eq('status', 'published')
            .order('created_at', { ascending: false });

          if (!loadError && loadData) {
            setLoadOrders(loadData);
          }
        }
      } catch (err) {
        setError('Failed to fetch customer data');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [username, user]);

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'completed': return 'default';
      case 'published': return 'secondary';
      case 'draft': return 'outline';
      default: return 'secondary';
    }
  };

  const handleViewLoadOrder = (orderId: string) => {
    window.open(`/load/${orderId}`, '_blank');
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20">
        <div className="container mx-auto px-4 py-8">
          <Card className="mb-6">
            <CardHeader>
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-20" />
                ))}
              </div>
            </CardContent>
          </Card>
          
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">Customer Not Found</h3>
            <p className="text-muted-foreground">
              The requested customer profile could not be found.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background p-4 pb-32">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Customer Header - Skeleton Version */}
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="w-20 h-20 flex items-center justify-center bg-muted rounded-xl">
                <Building2 className="h-12 w-12 text-muted-foreground" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">***</h2>
            <p className="text-muted-foreground mb-2">@{customer.username || '***'}</p>
            <Badge variant="outline">***</Badge>
          </div>

          {/* Summary Cards - Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <Building2 className="h-6 w-6 mx-auto mb-2 text-primary" />
                <p className="text-xs text-muted-foreground">Organization Type</p>
                <p className="font-bold text-sm">***</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Truck className="h-6 w-6 mx-auto mb-2 text-primary" />
                <p className="text-xs text-muted-foreground">Active Loads</p>
                <p className="font-bold text-lg">***</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="w-6 h-6 bg-blue-500 rounded-full mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Connections</p>
                <p className="font-bold text-lg">***</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center py-12">
            <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">Authentication Required</h3>
            <p className="text-muted-foreground mb-4">
              Please log in to view this customer's available loads.
            </p>
            <Button onClick={() => window.location.href = '/auth'}>
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 pb-32">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Customer Header */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-20 h-20 flex items-center justify-center bg-primary rounded-xl">
              <Building2 className="h-12 w-12 text-primary-foreground" />
            </div>
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">{customer.name}</h2>
          <p className="text-muted-foreground mb-2">@{customer.username}</p>
          <Badge variant="outline">{customer.type.replace('_', ' ').toUpperCase()}</Badge>
        </div>

        {customer.description && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">{customer.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Building2 className="h-6 w-6 mx-auto mb-2 text-primary" />
              <p className="text-xs text-muted-foreground">Organization Type</p>
              <p className="font-bold text-sm">{customer.type.replace('_', ' ')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Truck className="h-6 w-6 mx-auto mb-2 text-primary" />
              <p className="text-xs text-muted-foreground">Active Loads</p>
              <p className="font-bold text-lg">{loadOrders.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="w-6 h-6 bg-blue-500 rounded-full mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Connections</p>
              <p className="font-bold text-lg">{customer.active_connections_count}</p>
            </CardContent>
          </Card>
        </div>

        {/* Load Orders Grid */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Available Loads</h3>
          
          {loadOrders.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No Active Loads</h3>
                <p className="text-muted-foreground">
                  This customer doesn't have any published loads at the moment.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {loadOrders.map((order) => (
                <Card key={order.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewLoadOrder(order.id)}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h4 className="text-lg font-semibold text-foreground">{order.title}</h4>
                          <Badge variant={getStatusBadgeVariant(order.status)}>
                            {order.status.toUpperCase()}
                          </Badge>
                        </div>
                        
                        {order.description && (
                          <p className="text-sm text-muted-foreground mb-4">{order.description}</p>
                        )}
                        
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                          <div className="text-center">
                            <Truck className="h-4 w-4 mx-auto mb-1 text-primary" />
                            <p className="text-xs text-muted-foreground">Vehicles</p>
                            <p className="text-sm font-bold">{order.vehicles_needed}</p>
                          </div>
                          <div className="text-center">
                            <div className="w-4 h-4 bg-blue-500 rounded-full mx-auto mb-1" />
                            <p className="text-xs text-muted-foreground">Assigned</p>
                            <p className="text-sm font-bold">{order.vehicles_assigned}</p>
                          </div>
                          <div className="text-center">
                            <Package className="h-4 w-4 mx-auto mb-1 text-primary" />
                            <p className="text-xs text-muted-foreground">Cargo</p>
                            <p className="text-sm font-bold">{order.cargo_type}</p>
                          </div>
                          <div className="text-center">
                            <Calendar className="h-4 w-4 mx-auto mb-1 text-primary" />
                            <p className="text-xs text-muted-foreground">Posted</p>
                            <p className="text-sm font-bold">
                              {format(new Date(order.created_at), 'MMM d')}
                            </p>
                          </div>
                          <div className="text-center">
                            <ExternalLink className="h-4 w-4 mx-auto mb-1 text-primary" />
                            <p className="text-xs text-muted-foreground">Action</p>
                            <p className="text-sm font-bold">View</p>
                          </div>
                        </div>
                        
                        {order.load_routes.length > 0 && (
                          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            <span>
                              {formatRoute(order.load_routes[0].pickup_city, order.load_routes[0].delivery_city)}
                              {order.load_routes.length > 1 && ` (+${order.load_routes.length - 1} more routes)`}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};